import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/api";
import useFloorSearchState from "@/hooks/useFloorSearchState";
import { clearAnnouncementDismissals } from "@/utils/announcementSession";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anonymous, obj = logged
  const [ready, setReady] = useState(false);
  const { floorSearches, setFloorSearch, clearFloorSearches } = useFloorSearchState(user);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    clearAnnouncementDismissals();
    setUser(data);
    return data;
  };

  const logout = async () => {
    clearFloorSearches();
    clearAnnouncementDismissals();
    // Clear all presence records first so other users stop seeing stale
    // "currently working" warnings immediately, regardless of whether the
    // server-side logout can decode the token.
    try { await api.delete("/presence/release-all"); } catch {}
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  // SaaS Foundation: allows /register to hydrate the auth context
  // after a successful company registration returned an auth cookie.
  const setUserFromAuth = (u) => {
    clearAnnouncementDismissals();
    setUser(u || false);
  };

  // Refresh the current user from /auth/me — call after any profile change
  // so the sidebar avatar, name, and other derived UI update immediately.
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      // If /auth/me fails the session has expired; log out silently.
      setUser(false);
      return null;
    }
  }, []);

  return (
    <AuthCtx.Provider value={{ user, ready, login, logout, setUserFromAuth, refreshUser, floorSearches, setFloorSearch }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
